import { produce } from "solid-js/store";
import {
  type SetStreamingStore,
  type StreamingBlockId,
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingMessageId,
  StreamingMessageStatus,
  type StreamingOperationId,
  type StreamingStore,
} from "./streamingTypes";

/**
 * Unloads a chat from the store, including all messages, operations, and blocks
 */
export const cleanupChat = (setStore: SetStreamingStore, chatId: StreamingChatId) => {
  setStore(
    produce((store) => {
      _mutatingCleanupMessages(store, store.chats[chatId]?.messages);
      store.chats[chatId] = undefined;
    }),
  );
};

/**
 * Unloads a message from the store, including all operations and blocks
 */
export const cleanupMessage = (setStore: SetStreamingStore, messageId: StreamingMessageId) => {
  setStore(
    produce((store) => {
      _mutatingCleanupMessages(store, [messageId]);
    }),
  );
};

/**
 * Computed based on the status of the messages
 */
export const getChatStatus = (store: StreamingStore, chatId: StreamingChatId): StreamingChatStatus => {
  const messages = store.chats[chatId]?.messages;
  if (!messages) {
    return StreamingChatStatus.IDLE;
  }
  // If any message is generating, the chat is generating
  for (const messageId of messages) {
    const message = store.messages[messageId];
    if (message?.status === StreamingMessageStatus.GENERATING) {
      return StreamingChatStatus.GENERATING;
    }
  }
  return StreamingChatStatus.IDLE;
};

// Helpers
const _mutatingCleanupBlocks = (store: StreamingStore, blocks?: StreamingBlockId[]) => {
  for (const blockId of blocks ?? []) {
    store.blocks[blockId] = undefined;
  }
};
const _mutatingCleanupOperations = (store: StreamingStore, operations?: StreamingOperationId[]) => {
  for (const operationId of operations ?? []) {
    const operation = store.operations[operationId];
    if (operation?.blocks?.length) {
      _mutatingCleanupBlocks(store, operation.blocks);
    }
    store.operations[operationId] = undefined;
  }
};
const _mutatingCleanupMessages = (store: StreamingStore, messages?: StreamingMessageId[]) => {
  for (const messageId of messages ?? []) {
    const message = store.messages[messageId];
    if (message?.operations?.length) {
      _mutatingCleanupOperations(store, message.operations);
    }
    store.messages[messageId] = undefined;
  }
};
